import { useState, useRef } from "react";

import { saveAs } from "file-saver";
import * as pdfjsLib from "pdfjs-dist";
import HeaderSection from "../../components/HeaderSection/HeaderSection";
import BottomSection from "../../components/BottomSection/BottomSection";
import { useTheme } from "../../context/ThemeContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faImage } from "@fortawesome/free-solid-svg-icons";

const PdfToWordConverter = () => {
    const [pdfFile, setPdfFile] = useState(null);
    const { darkMode } = useTheme();
    const [fileName, setFileName] = useState("");
    const [isConverting, setIsConverting] = useState(false);
    const [error, setError] = useState("");
    const fileInputRef = useRef(null);

    // Initialize PDF.js worker
    pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

    const handleFileUpload = (e) => {
        setError("");
        const file = e.target.files[0];
        if (!file) return;

        if (file.type !== "application/pdf") {
            setError("Please upload a valid PDF file.");
            return;
        }

        setPdfFile(file);
        setFileName(file.name.replace(/\.[^/.]+$/, "")); // Remove extension
    };

    const convertPdfToWord = async () => {
        if (!pdfFile) {
            setError("Please upload a PDF file first.");
            return;
        }

        setIsConverting(true);
        setError("");

        try {
            const arrayBuffer = await pdfFile.arrayBuffer();
            const loadingTask = pdfjsLib.getDocument(arrayBuffer);
            const pdf = await loadingTask.promise;

            let fullText = "";

            // Extract text from each page
            for (let i = 1; i <= pdf.numPages; i++) {
                const page = await pdf.getPage(i);
                const textContent = await page.getTextContent();
                const pageText = textContent.items
                    .map((item) => item.str)
                    .join(" ");
                fullText += pageText + "\n\n";
            }

            // Create Word document content
            const docxContent = `
        <html xmlns:o='urn:schemas-microsoft-com:office:office' 
              xmlns:w='urn:schemas-microsoft-com:office:word' 
              xmlns='http://www.w3.org/TR/REC-html40'>
          <head>
            <title>${fileName}</title>
            <meta charset="UTF-8">
            <!--[if gte mso 9]>
            <xml>
              <w:WordDocument>
                <w:View>Print</w:View>
                <w:Zoom>100</w:Zoom>
                <w:DoNotOptimizeForBrowser/>
              </w:WordDocument>
            </xml>
            <![endif]-->
          </head>
          <body>
            <div>${fullText.replace(/\n/g, "</div><div>")}</div>
          </body>
        </html>
      `;

            // Create Blob and download
            const blob = new Blob([docxContent], {
                type: "application/msword",
            });
            saveAs(blob, `${fileName}.doc`);
        } catch (err) {
            setError("Error converting PDF to Word. Please try again.");
            console.error("Conversion error:", err);
        } finally {
            setIsConverting(false);
        }
    };

    const triggerFileInput = () => {
        fileInputRef.current.click();
    };

    return (
        <>
            <HeaderSection toolId="pdfToWord" />
            <div className=" max-w-4xl mx-auto p-4 lg:max-w-full lg:px-4 dark:text-white">
                <div className="bg-white rounded-xl dark:bg-gray-800 shadow-sm dark:shadow-gray-900/10 border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 text-white">
                        <h1 className="text-2xl font-bold mb-2 flex items-center">
                            <FontAwesomeIcon icon={faImage} className="mr-3" />
                            PDF to Words Converter
                        </h1>
                        <p className="opacity-90">
                            Convert PDF to Word with comprehensive validation
                            checks
                        </p>
                    </div>

                    <div className="space-y-4 p-6 ">
                        <div className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-6 m-6">
                            <input
                                type="file"
                                ref={fileInputRef}
                                accept=".pdf"
                                onChange={handleFileUpload}
                                className="hidden"
                            />
                            <button
                                onClick={triggerFileInput}
                                className="bg-[#74D19C] hover:bg-[#74D19C] text-white font-medium py-2 px-4 rounded-md transition duration-200"
                            >
                                Upload PDF File
                            </button>
                            <p className="mt-2 text-sm text-gray-500">
                                Supports .pdf files
                            </p>
                            {fileName && (
                                <p className="mt-2 text-sm text-gray-700">
                                    File:{" "}
                                    <span className="font-medium">
                                        {fileName}.pdf
                                    </span>
                                </p>
                            )}
                        </div>

                        <button
                            onClick={convertPdfToWord}
                            disabled={!pdfFile || isConverting}
                            className={`m-6 px-3 py-2 mx-auto w-[100%] rounded-md text-white font-medium ${
                                !pdfFile
                                    ? "bg-gray-400 cursor-not-allowed"
                                    : "bg-green-500 hover:bg-green-600"
                            } transition duration-200`}
                        >
                            {isConverting ? (
                                <span className="flex items-center justify-center">
                                    <svg
                                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                    >
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                        ></circle>
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                        ></path>
                                    </svg>
                                    Converting...
                                </span>
                            ) : (
                                "Convert to Word"
                            )}
                        </button>

                        {error && (
                            <div className="bg-red-50 border-l-4 border-red-500 p-4">
                                <div className="flex">
                                    <div className="flex-shrink-0">
                                        <svg
                                            className="h-5 w-5 text-red-500"
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 20 20"
                                            fill="currentColor"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                    </div>
                                    <div className="ml-3">
                                        <p className="text-sm text-red-700">
                                            {error}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <BottomSection toolId="pdfToWord" />
        </>
    );
};

export default PdfToWordConverter;
